import { ProductSelectEvent } from "@shopify/events";

/**
 * <product-variant-picker> switches the selected variant of a product.
 *
 * It's theme-agnostic: on option change it dispatches `shopify:product:select`, re-renders its
 * section for the new option values (Section Rendering API) and swaps every
 * `[data-variant-update]` element in the section with its counterpart from the response.
 *
 * To refresh more elements on variant switch, add `data-variant-update="<key>"` to them:
 * - the key must be unique within the section (suffix it with `block.id`),
 * - the element must render for every variant (wrap conditional markup instead of tagging it),
 * - it can live in any block of the same section; nested tagged elements are swapped with their parent.
 * - an element with an `updateFrom(rendered)` method (e.g. <product-media>) updates itself from its
 *   rendered counterpart instead of being replaced, so it can animate the change.
 *
 * The event's promise resolves with `{ variant, detail }`, `variant` being read from the
 * `[data-variant-json]` script after the swap. Superseded or failed requests reject it.
 *
 * Attributes:
 * - `data-section-id`, `data-product-url`: what to re-render.
 * - `data-product-id`, `data-product-title`, `data-product-handle`: the event's `product`.
 * - `data-update-url`: when present, reflects the selected variant in the page URL.
 *
 * Tagged elements are swapped within the section, or within the closest `[data-variant-scope]`
 * ancestor when the picker is shown outside it (e.g. the quick add dialog).
 *
 * Sets `aria-busy="true"` on itself while a request is in flight.
 */
class ProductVariantPicker extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.addEventListener("change", this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.#onChange);
    this.#abortController?.abort();
  }

  #onChange = (event) => {
    if (!event.target.matches('input[type="radio"]')) return;

    this.select();
  };

  get #section() {
    return (
      this.closest("[data-variant-scope]") ||
      this.closest(`#shopify-section-${this.dataset.sectionId}`) ||
      document
    );
  }

  async select() {
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const inputs = [...this.querySelectorAll('input[type="radio"]:checked')];
    const deferred = ProductSelectEvent.createPromise();
    const detail = {
      source: "product-variant-picker",
      sectionId: this.dataset.sectionId,
    };
    const event = new ProductSelectEvent({
      product: {
        id: this.dataset.productId,
        title: this.dataset.productTitle,
        handle: this.dataset.productHandle,
      },
      selectedOptions: inputs.map((input) => ({
        name: input.dataset.optionName,
        value: input.value,
      })),
      promise: deferred.promise,
      detail,
    });

    this.setAttribute("aria-busy", "true");
    this.dispatchEvent(event);
    // Superseded requests reject: keep that from surfacing as an unhandled rejection
    event.promise?.catch(() => {});

    try {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set(
        "option_values",
        inputs.map((input) => input.dataset.optionValueId).join(","),
      );
      url.searchParams.set("section_id", this.dataset.sectionId);

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok)
        throw new Error(`Section request failed with status ${response.status}`);

      const html = new DOMParser().parseFromString(
        await response.text(),
        "text/html",
      );
      this.#swap(html);

      const variant = this.#readVariant();
      if (variant && this.hasAttribute("data-update-url")) {
        const pageUrl = new URL(window.location.href);
        pageUrl.searchParams.set("variant", variant.id);
        window.history.replaceState(window.history.state, "", pageUrl);
      }

      deferred.resolve({ variant, detail });
    } catch (error) {
      if (error.name !== "AbortError") console.error(error);
      deferred.reject(error);
    } finally {
      if (this.#abortController === controller) {
        this.#abortController = null;
        this.removeAttribute("aria-busy");
      }
    }
  }

  /**
   * Replaces every tagged element in the section with its counterpart from the rendered section.
   */
  #swap(html) {
    const focusedId = document.activeElement?.id;

    for (const element of this.#section.querySelectorAll("[data-variant-update]")) {
      // Swapped along with its tagged ancestor
      if (element.parentElement?.closest("[data-variant-update]")) continue;

      const key = CSS.escape(element.dataset.variantUpdate);
      const replacement = html.querySelector(`[data-variant-update="${key}"]`);
      if (!replacement) continue;

      if (
        typeof element.updateFrom === "function" &&
        element.localName === replacement.localName
      )
        element.updateFrom(replacement);
      else element.replaceWith(replacement);
    }

    if (focusedId) document.getElementById(focusedId)?.focus();
  }

  #readVariant() {
    const script = this.querySelector("[data-variant-json]");
    if (!script) return null;

    return JSON.parse(script.textContent);
  }
}

if (!customElements.get("product-variant-picker")) {
  customElements.define("product-variant-picker", ProductVariantPicker);
}
